import { AsyncPipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatIconButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { MatMenu, MatMenuTrigger } from '@angular/material/menu'
import {
  ElderBasicPaneLayoutComponent,
  ElderNavLinkComponent,
  ElderNavListComponent,
  ElderPaneActionsComponent,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneTitleComponent,
  ElderToastService,
  Pageable,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject } from 'rxjs'
import { FilterBuilder } from '../../../orders/filter-builder'
import { CapabilitySpec } from '../../../process/capability-spec/capabilities/capability-spec'
import { CapabilitySpecService } from '../../../process/capability-spec/capability-spec.service'
import { ProcessingTimeSpec } from '../../../process/capability-spec/capacities/processing-time-spec'
import { CapabilityConf } from '../capability/capabilities/capability-config'
import { CapabilityDetailComponent } from '../capability/capabilities/capability-detail/capability-detail.component'
import { SkillSetConf } from '../capability/skillsets/skill-set-conf'
import { WorkCenter } from '../work-center'

@Component({
  selector: 'app-work-center-detail-capabilities',
  templateUrl: './work-center-detail-capabilities.component.html',
  styleUrls: ['./work-center-detail-capabilities.component.scss'],
  imports: [
    FormsModule,
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    ElderPaneHeaderComponent,
    ElderPaneTitleComponent,
    ElderPaneActionsComponent,
    ElderPaneContentComponent,
    ElderNavListComponent,
    ElderNavLinkComponent,
    MatIconButton,
    MatIcon,
    MatMenuTrigger,
    MatMenu,
    CapabilityDetailComponent,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class WorkCenterDetailCapabilitiesComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  public readonly workCenter$ = new BehaviorSubject<WorkCenter>(null)

  public readonly capabilities$ = new BehaviorSubject<CapabilityConf[]>([])
  public readonly selectedCapability$ = new BehaviorSubject<CapabilityConf>(null)

  @Output()
  public save: EventEmitter<Event> = new EventEmitter<Event>()
  private capabilityService = inject(CapabilitySpecService)
  private toastService = inject(ElderToastService)

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set workCenter(workCenter: WorkCenter) {
    this.workCenter$.next(workCenter)

    if (workCenter && workCenter.type && workCenter?.capabilities?.length > 0) {
      this.capabilityService
        .findAllPaged(new Pageable(0, 100), [
          FilterBuilder.ids(workCenter.capabilities.map((c) => c.capabilitySpecId)),
        ])
        .subscribe({
          next: (specs) => {
            this.log.info('Loaded specs: ', specs.content)
            this.populate(workCenter.capabilities, specs.content)
            this.capabilities$.next(workCenter.capabilities)
            this.autoSelectCapability()
          },
          error: (err) => this.toastService.error('Failed to load capability specs!', err),
        })
    }
  }

  public set selectedCapability(capability: CapabilityConf) {
    this.selectedCapability$.next(capability)
  }

  public get selectedCapability(): CapabilityConf {
    return this.selectedCapability$.getValue()
  }

  /***************************************************************************
   *                                                                         *
   * Component Events                                                        *
   *                                                                         *
   **************************************************************************/

  public onSave(event: Event): void {
    this.save.emit(event)
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public compare(a: CapabilityConf, b: CapabilityConf): boolean {
    return !!a && !!b && a.name === b.name
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private autoSelectCapability(): void {
    const caps = this.capabilities$.getValue()
    if (caps && caps.length > 0) {
      const autoSelected = caps.find(
        (c) => c.capabilitySpecId === this.selectedCapability?.capabilitySpecId
      )
      this.selectedCapability = autoSelected ? autoSelected : caps[0]
    } else {
      this.selectedCapability = null
    }
  }

  private populate(capabilities: CapabilityConf[], specs: CapabilitySpec[]): void {
    for (const capability of capabilities) {
      const capSpec = specs.find((spec) => spec.id === capability.capabilitySpecId)

      if (capSpec) {
        for (const skillSet of capability.skillSets) {
          this.populateSkillSet(skillSet, capSpec)
        }
      } else {
        this.log.warn(
          'Failed to populate. Could not find CapabilitySpec with id ' + capability.capabilitySpecId
        )
      }
    }
  }

  private populateSkillSet(skillSet: SkillSetConf, capSpec: CapabilitySpec): void {
    const skillSetSpec = capSpec.skillSets.find((spec) => spec.id === skillSet.skillSetSpecId)
    skillSet.spec = skillSetSpec

    if (!!skillSetSpec) {
      for (const condition of skillSet.conditions) {
        condition.spec = skillSetSpec.conditions.find(
          (spec) => spec.id === condition.constraintSpecId
        )
      }

      for (const constraint of skillSet.constraints) {
        constraint.spec = skillSetSpec.constraints.find(
          (spec) => spec.id === constraint.constraintSpecId
        )
      }

      const pTimeSpecs = ([] as ProcessingTimeSpec[]).concat(...skillSetSpec.capacities)

      for (const variantSet of skillSet.capacities) {
        for (const processingTime of variantSet) {
          processingTime.spec = pTimeSpecs.find((spec) => spec.id === processingTime.specId)
        }
      }
    }
  }
}
